import { useState } from 'react';
import {
  eventosEnVivo,
  getEventosDestacados,
  getEventosProximos,
  getMasParticipantes,
  getMasCompartidos,
  getMasComentados,
  getMasProximos,
  tiposEventoEnVivo,
  reglasEventosEnVivo,
  scoreEventosEnVivo,
  statsEventosEnVivo,
  formatearNumero,
  type EventoEnVivo,
} from '@/mocks/eventosEnVivo';

function EventoCard({ evento }: { evento: EventoEnVivo }) {
  const tipoInfo = tiposEventoEnVivo.find(t => t.id === evento.tipo);

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 hover:border-accent-200/60 transition-all duration-300 overflow-hidden group cursor-pointer">
      <div className="relative h-36 overflow-hidden">
        <img src={evento.imagenUrl} alt={evento.titulo} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          {evento.esDestacado && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              DESTACADO
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${tipoInfo?.bg} ${tipoInfo?.color}`}>
            {tipoInfo?.label}
          </span>
        </div>
        <div className="absolute bottom-2.5 left-2.5 right-2.5">
          <p className="text-white font-bold text-xs leading-snug line-clamp-2">{evento.titulo}</p>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1">
            <img src={evento.radioLogo} alt="" className="w-4 h-4 rounded-full object-cover" />
            {evento.radioNombre}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line text-[9px]"></i>
            {evento.ciudad}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="flex items-center gap-1 font-semibold text-rose-600">
            <i className="ri-timer-line text-[9px]"></i>
            {evento.cuentaRegresiva}
          </span>
          <span className="text-foreground-400">{evento.hora}</span>
        </div>
        <div className="flex items-center justify-between text-[10px] text-foreground-400">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center gap-1"><i className="ri-group-line text-[9px]"></i>{formatearNumero(evento.participantes)}</span>
            <span className="flex items-center gap-1"><i className="ri-share-forward-line text-[9px]"></i>{formatearNumero(evento.compartidos)}</span>
            <span className="flex items-center gap-1"><i className="ri-chat-3-line text-[9px]"></i>{formatearNumero(evento.comentarios)}</span>
          </div>
        </div>
        {evento.locutorNombre && (
          <div className="flex items-center gap-2 pt-2 border-t border-background-200/70">
            <img src={evento.locutorAvatar} alt={evento.locutorNombre} className="w-5 h-5 rounded-full object-cover" />
            <span className="text-[10px] text-foreground-500">{evento.locutorNombre}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedEventoCard({ evento }: { evento: EventoEnVivo }) {
  const tipoInfo = tiposEventoEnVivo.find(t => t.id === evento.tipo);

  return (
    <div className="rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-300 cursor-pointer">
      <div className="relative h-48 overflow-hidden">
        <img src={evento.imagenUrl} alt={evento.titulo} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500 text-white text-[9px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            DESTACADO
          </span>
          <span className={`px-2.5 py-1 rounded-full text-[9px] font-medium border ${tipoInfo?.bg} ${tipoInfo?.color}`}>
            {tipoInfo?.label}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white font-bold text-sm leading-snug line-clamp-2">{evento.titulo}</p>
          <p className="text-white/70 text-[10px] mt-1">{evento.radioNombre} · {evento.ciudad}</p>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <p className="text-[11px] text-foreground-600 leading-relaxed line-clamp-2">{evento.descripcion}</p>
        <div className="flex items-center justify-between text-[10px]">
          <span className="flex items-center gap-1 font-semibold text-rose-600">
            <i className="ri-timer-line text-[9px]"></i>
            {evento.cuentaRegresiva}
          </span>
          <div className="flex items-center gap-3 text-foreground-400">
            <span className="flex items-center gap-1"><i className="ri-group-line text-[9px]"></i>{formatearNumero(evento.participantes)}</span>
            <span className="flex items-center gap-1"><i className="ri-share-forward-line text-[9px]"></i>{formatearNumero(evento.compartidos)}</span>
            <span className="flex items-center gap-1"><i className="ri-chat-3-line text-[9px]"></i>{formatearNumero(evento.comentarios)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 pt-1.5 border-t border-background-200/70">
          {evento.tags.map(tag => (
            <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[9px] text-foreground-500">#{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function EventosEnVivoSection() {
  const [activeTab, setActiveTab] = useState<'destacados' | 'proximos' | 'participantes' | 'compartidos' | 'reglas'>('destacados');

  const destacados = getEventosDestacados().slice(0, 6);
  const proximos = getEventosProximos().slice(0, 6);
  const masParticipantes = getMasParticipantes().slice(0, 6);
  const masCompartidos = getMasCompartidos().slice(0, 6);

  const tabs = [
    { id: 'destacados' as const, label: 'Destacados', icon: 'ri-sparkling-line', count: destacados.length },
    { id: 'proximos' as const, label: 'Próximos', icon: 'ri-timer-line', count: proximos.length },
    { id: 'participantes' as const, label: 'Más Participantes', icon: 'ri-group-line', count: masParticipantes.length },
    { id: 'compartidos' as const, label: 'Más Compartidos', icon: 'ri-share-forward-line', count: masCompartidos.length },
    { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line', count: reglasEventosEnVivo.length },
  ];

  const currentEvents = activeTab === 'destacados' ? destacados
    : activeTab === 'proximos' ? proximos
    : activeTab === 'participantes' ? masParticipantes
    : masCompartidos;

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            EVENTOS EN VIVO
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Transmisiones Especiales <span className="text-rose-600">que No te Podés Perder</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Entrevistas, conciertos, coberturas, festivales y especiales. Cada evento con cuenta regresiva, participantes, compartidos y comentarios. Los eventos se destacan automáticamente.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Eventos', value: statsEventosEnVivo.totalEventos.toString(), icon: 'ri-calendar-event-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Hoy', value: statsEventosEnVivo.eventosHoy.toString(), icon: 'ri-calendar-check-line', color: 'text-red-600 bg-red-50' },
            { label: 'Participantes', value: formatearNumero(statsEventosEnVivo.totalParticipantes), icon: 'ri-group-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Compartidos', value: formatearNumero(statsEventosEnVivo.totalCompartidos), icon: 'ri-share-forward-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Comentarios', value: formatearNumero(statsEventosEnVivo.totalComentarios), icon: 'ri-chat-3-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tipo Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 mb-8">
          {tiposEventoEnVivo.map(tipo => (
            <span
              key={tipo.id}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border ${tipo.bg} ${tipo.color}`}
            >
              <i className={`${tipo.icon} text-[10px]`}></i>
              {tipo.label}
            </span>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab !== 'reglas' ? (
          <>
            {/* Featured cards for Destacados */}
            {activeTab === 'destacados' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                {(currentEvents as EventoEnVivo[]).slice(0, 2).map(evento => (
                  <FeaturedEventoCard key={evento.id} evento={evento} />
                ))}
              </div>
            )}
            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(activeTab === 'destacados'
                ? (currentEvents as EventoEnVivo[]).slice(2)
                : currentEvents as EventoEnVivo[]
              ).map(evento => (
                <EventoCard key={evento.id} evento={evento} />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Reglas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {reglasEventosEnVivo.map(regla => (
                <div key={regla.id} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${regla.gradiente} flex items-center justify-center mb-3`}>
                    <i className={`${regla.icono} text-white text-lg`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-1.5">{regla.titulo}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>

            {/* Score */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {scoreEventosEnVivo.map(item => (
                <div key={item.accion} className="p-4 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${item.gradiente} flex items-center justify-center mx-auto mb-2`}>
                    <i className={`${item.icono} text-white text-sm`}></i>
                  </div>
                  <p className="text-lg font-extrabold text-foreground-950">+{item.puntos}</p>
                  <p className="text-[10px] font-semibold text-foreground-700">{item.accion}</p>
                  <p className="text-[9px] text-foreground-400 mt-1">{item.descripcion}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href="/eventos-en-vivo"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-calendar-event-line"></i>
            Ver Todos los Eventos en Vivo
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-xs text-foreground-500 mt-3">
            Descubrí las transmisiones especiales de todas las radios RFW
          </p>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-rose-50 border border-rose-200 text-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-sparkling-line text-xl text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">Los eventos deben destacarse automáticamente.</p>
          <p className="text-xs text-foreground-600 max-w-xl mx-auto">
            Cuando un evento alcanza cierto umbral de participantes o está próximo a comenzar, se destaca automáticamente en toda la plataforma. La comunidad decide qué es importante — el sistema lo amplifica.
          </p>
        </div>
      </div>
    </section>
  );
}